import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams } from './viewerAccess';
import { publishCollectionChange } from '../utils/collectionSync';
import { readRegistryCollection, writeRegistryCollection } from '../component/Admin/data/registryStore';
import { ageClassApi, breedEcotypeApi, speciesApi } from './speciesLivestockRegistryApi';
import { extractCollectionResponse } from './collectionResponse';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';

const DELETED_CACHE_KEY = 'animalRegistrationsDeleted';

const TAG_TYPE_LABELS = {
  electronic_rfid: 'Electronic RFID',
  visual_ear_tag: 'Visual Ear Tag',
  bolus: 'Bolus',
  brand: 'Brand',
};

const TAG_STATUS_LABELS = {
  active: 'Active',
  lost: 'Lost',
  damaged: 'Damaged',
  replaced: 'Replaced',
};

const EMPTY_LOOKUPS = {
  species: new Map(),
  breeds: new Map(),
  ageClasses: new Map(),
};

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData, normalize) {
  return extractCollectionResponse(responseData, normalize);
}

function extractItem(responseData, normalize, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => item?.uuid === targetUuid)
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (
      candidate.uuid ||
      candidate.animal_tag_id ||
      candidate.animalTagId ||
      candidate.tagId ||
      candidate.tag_id
    ) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function makeCollectionApi({ basePath, normalize, buildPayload, defaultPerPage = 25, collectionKey }) {
  const syncKey = collectionKey ?? basePath.replace(/^\/v1\//, '').replace(/^\//, '').replace(/\/$/, '');

  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    return extractCollection(data, normalize);
  }

  return {
    collectionKey: syncKey,

    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      const item = extractItem(data, normalize);
      publishCollectionChange(syncKey, { action: 'create', uuid: item?.uuid ?? null });
      return item;
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'update', uuid });
      return item;
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      publishCollectionChange(syncKey, { action: 'remove', uuid });
      return unwrapResponse(data);
    },

    async submit(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/submit`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'submit', uuid });
      return item;
    },

    async validate(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/validate`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'validate', uuid });
      return item;
    },

    async approve(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/approve`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'approve', uuid });
      return item;
    },

    async reject(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/reject`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'reject', uuid });
      return item;
    },
  };
}

function normalizeCode(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, '_');
}

function humanizeCode(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeTagType(value) {
  const key = normalizeCode(value);
  return TAG_TYPE_LABELS[key] ?? humanizeCode(value);
}

function normalizeTagStatus(value) {
  const key = normalizeCode(value);
  return TAG_STATUS_LABELS[key] ?? humanizeCode(value);
}

function toTagTypeCode(value) {
  const key = normalizeCode(value);
  return TAG_TYPE_LABELS[key] ? key : key || null;
}

function toTagStatusCode(value) {
  const key = normalizeCode(value);
  return key || null;
}

function normalizeDateValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const text = String(value).trim();
  if (!text) return null;

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const dmyMatch = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const slashMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function firstText(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

function lookupLabel(map, ...keys) {
  if (!map || typeof map.get !== 'function') return '';
  for (const key of keys) {
    if (key === null || key === undefined || key === '') continue;
    const label = map.get(String(key));
    if (label) return label;
  }
  return '';
}

function buildLookupMap(items = [], valueKeys = [], labelKeys = []) {
  const map = new Map();

  items.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const values = valueKeys
      .map((key) => item[key])
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== '');

    const label =
      labelKeys
        .map((key) => item[key])
        .find((value) => value !== null && value !== undefined && String(value).trim() !== '') ??
      values[0];

    values.forEach((value) => {
      map.set(String(value), String(label));
    });
  });

  return map;
}

function resolveDisplayValue(row, lookups, keys, fallback = '') {
  const direct = keys.map((key) => row[key]).find((value) => value !== null && value !== undefined && String(value).trim() !== '');
  if (direct !== undefined && direct !== null && String(direct).trim() !== '') {
    return String(direct);
  }

  const lookupValue = lookupLabel(lookups, ...keys.map((key) => row[key]));
  if (lookupValue) return lookupValue;

  return fallback;
}

function normalizeAnimalRegistration(row = {}, lookups = EMPTY_LOOKUPS) {
  const speciesObj = row.species && typeof row.species === 'object' ? row.species : {};
  const breedObj = row.breed && typeof row.breed === 'object' ? row.breed : {};
  const ageClassObj = row.age_class && typeof row.age_class === 'object' ? row.age_class : row.ageClass && typeof row.ageClass === 'object' ? row.ageClass : {};
  const ownerObj = row.owner && typeof row.owner === 'object' ? row.owner : {};

  const animalTagId = firstText(
    row.animal_tag_id,
    row.animalTagId,
    row.tag_id,
    row.tagId,
    row.tag_number,
    row.tagNumber
  );

  const tagType = normalizeTagType(
    firstText(row.tag_type, row.tagType, row.tagging_type, row.taggingType, row.type)
  );

  const tagStatus = normalizeTagStatus(
    firstText(row.tag_status, row.tagStatus, row.status)
  );

  const speciesUuid = firstText(
    row.species_uuid,
    row.speciesUuid,
    speciesObj.uuid,
    speciesObj.id
  );
  const speciesLabel = firstText(
    row.species_label,
    row.speciesLabel,
    typeof row.species === 'string' ? row.species : '',
    speciesObj.common_name_nigeria,
    speciesObj.commonNameNigeria,
    speciesObj.species_name,
    speciesObj.speciesName,
    speciesObj.name,
    speciesObj.label,
    lookupLabel(lookups.species, speciesUuid),
    row.species_code,
    row.speciesCode,
    speciesUuid
  );

  const breedUuid = firstText(
    row.breed_uuid,
    row.breedUuid,
    breedObj.uuid,
    breedObj.id
  );
  const breedLabel = firstText(
    row.breed_label,
    row.breedLabel,
    typeof row.breed === 'string' ? row.breed : '',
    breedObj.breed_name,
    breedObj.breedName,
    breedObj.breed_code,
    breedObj.breedCode,
    breedObj.name,
    breedObj.label,
    lookupLabel(lookups.breeds, breedUuid),
    row.breed_code,
    row.breedCode,
    breedUuid
  );

  const ageClassUuid = firstText(
    row.age_class_uuid,
    row.ageClassUuid,
    ageClassObj.uuid,
    ageClassObj.id
  );
  const ageClassLabel = firstText(
    row.age_class_label,
    row.ageClassLabel,
    typeof row.age_class === 'string' ? row.age_class : '',
    typeof row.ageClass === 'string' ? row.ageClass : '',
    ageClassObj.age_class_label,
    ageClassObj.ageClassLabel,
    ageClassObj.label,
    ageClassObj.name,
    lookupLabel(lookups.ageClasses, ageClassUuid),
    row.age_class_code,
    row.ageClassCode,
    ageClassUuid
  );

  const holdingReference = firstText(
    row.holding_reference,
    row.holdingReference,
    row.herd_holding_reference,
    row.herdHoldingRef
  );

  const ownerUuid = firstText(row.owner_uuid, row.ownerUuid, ownerObj.uuid, ownerObj.id);
  const ownerName = firstText(
    row.owner_name,
    row.ownerName,
    typeof row.owner === 'string' ? row.owner : '',
    ownerObj.name,
    ownerObj.full_name,
    ownerObj.fullName,
    ownerObj.label,
    row.owner,
    ownerUuid
  );

  const state = firstText(row.state, row.state_name, row.stateName);
  const lga = firstText(row.lga, row.lga_name, row.lgaName);
  const naitsSource = firstText(row.naits_source, row.naitsSource, row.source, row.data_source);
  const registrationDate = firstText(
    row.registration_date,
    row.registrationDate,
    row.created_at,
    row.createdAt
  );

  return {
    ...row,
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    animal_tag_id: animalTagId,
    animalTagId,
    tagId: animalTagId,
    tag_type: firstText(row.tag_type, row.tagType),
    tagType,
    tag_status: firstText(row.tag_status, row.tagStatus),
    tagStatus,
    species_uuid: speciesUuid,
    speciesUuid,
    species_code: firstText(row.species_code, row.speciesCode, speciesObj.species_code, speciesObj.speciesCode),
    speciesCode: firstText(row.species_code, row.speciesCode, speciesObj.species_code, speciesObj.speciesCode),
    species: speciesLabel,
    speciesLabel,
    breed_uuid: breedUuid,
    breedUuid,
    breed_code: firstText(row.breed_code, row.breedCode, breedObj.breed_code, breedObj.breedCode),
    breedCode: firstText(row.breed_code, row.breedCode, breedObj.breed_code, breedObj.breedCode),
    breed: breedLabel,
    breedLabel,
    age_class_uuid: ageClassUuid,
    ageClassUuid,
    age_class_code: firstText(row.age_class_code, row.ageClassCode, ageClassObj.age_class_code, ageClassObj.ageClassCode),
    ageClassCode: firstText(row.age_class_code, row.ageClassCode, ageClassObj.age_class_code, ageClassObj.ageClassCode),
    age_class: ageClassLabel,
    ageClass: ageClassLabel,
    holding_reference: holdingReference,
    holdingReference,
    herdHoldingRef: holdingReference,
    owner_uuid: ownerUuid,
    ownerUuid,
    owner_name: firstText(row.owner_name, row.ownerName, ownerObj.name, ownerObj.full_name, ownerObj.fullName),
    ownerName,
    owner: ownerName,
    state,
    lga,
    naits_source: naitsSource,
    naitsSource,
    naitsRef: firstText(row.naits_reference, row.naitsReference, row.naitsRef, animalTagId),
    registration_date: registrationDate,
    registrationDate,
    created_at: row.created_at ?? row.createdAt ?? null,
    updated_at: row.updated_at ?? row.updatedAt ?? null,
    raw: row,
  };
}

function buildAnimalRegistrationPayload(payload = {}) {
  return {
    state: trimOrNull(payload.state),
    animal_tag_id: trimOrNull(payload.animal_tag_id ?? payload.animalTagId ?? payload.tagId),
    tag_type: toTagTypeCode(payload.tag_type ?? payload.tagType),
    tag_status: toTagStatusCode(payload.tag_status ?? payload.tagStatus),
    species_uuid: trimOrNull(payload.species_uuid ?? payload.speciesUuid),
    breed_uuid: trimOrNull(payload.breed_uuid ?? payload.breedUuid),
    sex: trimOrNull(payload.sex),
    age_class_uuid: trimOrNull(payload.age_class_uuid ?? payload.ageClassUuid),
    holding_reference: trimOrNull(payload.holding_reference ?? payload.holdingReference ?? payload.herdHoldingRef),
    owner_uuid: trimOrNull(payload.owner_uuid ?? payload.ownerUuid),
    lga: trimOrNull(payload.lga),
    naits_source: trimOrNull(payload.naits_source ?? payload.naitsSource),
  };
}

export async function loadAnimalRegistrationLookups() {
  const [speciesResult, breedResult, ageClassResult] = await Promise.allSettled([
    speciesApi.listAll({ per_page: 500 }),
    breedEcotypeApi.listAll({ per_page: 500 }),
    ageClassApi.listAll({ per_page: 500 }),
  ]);

  return {
    species:
      speciesResult.status === 'fulfilled'
        ? buildLookupMap(
            speciesResult.value.items ?? [],
            ['uuid', 'id'],
            ['common_name_nigeria', 'commonNameNigeria', 'species_code', 'speciesCode', 'name', 'label']
          )
        : new Map(),
    breeds:
      breedResult.status === 'fulfilled'
        ? buildLookupMap(
            breedResult.value.items ?? [],
            ['uuid', 'id'],
            ['breed_name', 'breedName', 'breed_code', 'breedCode', 'name', 'label']
          )
        : new Map(),
    ageClasses:
      ageClassResult.status === 'fulfilled'
        ? buildLookupMap(
            ageClassResult.value.items ?? [],
            ['uuid', 'id'],
            ['age_class_label', 'ageClassLabel', 'age_class_code', 'ageClassCode', 'name', 'label']
          )
        : new Map(),
  };
}

export function archiveDeletedAnimalRegistration(record = {}, response = null) {
  if (!record || typeof record !== 'object') return null;

  const timestamp = new Date().toISOString();
  const archived = {
    ...record,
    deleted_at: timestamp,
    deletedAt: timestamp,
    delete_response: response ?? null,
    deleteResponse: response ?? null,
  };

  const current = readRegistryCollection(DELETED_CACHE_KEY) ?? [];
  writeRegistryCollection(DELETED_CACHE_KEY, [archived, ...current]);
  return archived;
}

const animalRegistrationsBaseApi = makeCollectionApi({
  basePath: '/v1/animal-registrations',
  normalize: normalizeAnimalRegistration,
  buildPayload: buildAnimalRegistrationPayload,
  defaultPerPage: 25,
  collectionKey: 'animal-registrations',
});

export const animalRegistrationsApi = withOfflineCollectionSupport(animalRegistrationsBaseApi, {
  basePath: '/v1/animal-registrations',
  collectionKey: 'animal-registrations',
  normalize: normalizeAnimalRegistration,
  buildPayload: buildAnimalRegistrationPayload,
});

export {
  DELETED_CACHE_KEY,
  EMPTY_LOOKUPS,
  buildAnimalRegistrationPayload,
  buildLookupMap,
  cleanQueryValue,
  extractCollection,
  extractItem,
  makeCollectionApi,
  normalizeAnimalRegistration,
  normalizeDateValue,
  normalizeTagStatus,
  normalizeTagType,
  resolveDisplayValue,
};
